/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-null/no-null */
import { Autocomplete, Box, Chip, IconButton, Icon, SxProps, Theme, OutlinedInput } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { parseISO } from 'date-fns';
import { ReactElement, useCallback, useState, useRef, useEffect, Fragment, cloneElement, useMemo } from 'react';

import { SearchBoxAutocompleteLabelSegment } from './search-box-autocomplete-label-segment';
import { SearchBoxAutocompleteValueSegment } from './search-box-autocomplete-value-segment';
import { SearchBoxDateRangeSegment } from './search-box-date-range-segment';
import { SearchBoxTextSegment } from './search-box-text-segment';
import { SearchBoxAutocompleteValue, SearchBoxMenuOption, SearchBoxOptionValue } from './search-box-types';

interface SearchBoxProps {
    menuOptions: SearchBoxMenuOption[];
    name: string;
    onChange?: (newValue: SearchBoxAutocompleteValue[]) => void;
    value?: SearchBoxAutocompleteValue[];
    onSearch?: (newValue: SearchBoxAutocompleteValue[]) => void;
    sx?: SxProps<Theme>;
    useUrlSearchParams?: boolean;
}

const getSegmentIndex = (displayIndex: number, values: SearchBoxAutocompleteValue[]): number => {
    let segmentsCounter = 0;
    let segmentIndex = 0;
    while (segmentsCounter < displayIndex) {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        segmentsCounter += 'eq' === values[segmentIndex]?.optionRelation ? 2 : 1;
        segmentIndex++;
    }

    return segmentIndex;
};

const prepareInitialDisplayValue = (
    initialValue: SearchBoxAutocompleteValue[],
    options: SearchBoxMenuOption[],
): Array<SearchBoxMenuOption | string> => {
    const result: Array<SearchBoxMenuOption | string> = [];

    initialValue.forEach((element) => {
        if ('freeSolo' === element.optionRelation) {
            result.push(element.optionValue as string);
        } else {
            const selectedOption = options.find((option) => {
                return option.name === element.optionName;
            })!;
            result.push(selectedOption, { ...selectedOption, isPlaceholder: true });
        }
    });

    return result;
};

const getValueFromUrl = (name: string, menuOptions: SearchBoxMenuOption[]): SearchBoxAutocompleteValue[] => {
    const result: SearchBoxAutocompleteValue[] = [];
    const url = new URL(window.location.href);

    const searchBoxKeys = menuOptions.map(({ name: menuOptionName }) => menuOptionName);

    url.searchParams.forEach((value, key) => {
        if (key === name) {
            result.push({ optionName: name, optionRelation: 'freeSolo', optionValue: value });
        } else if (searchBoxKeys.includes(key)) {
            result.push({
                optionName: key,
                optionRelation: 'eq',
                optionValue: { value, label: url.searchParams.get(`${key}-label`)! },
            });
        } else {
            const advKey = key.split('-id')[0];
            if (searchBoxKeys.includes(advKey)) {
                result.push({
                    optionName: advKey,
                    optionRelation: 'eq',
                    optionValue: {
                        label: url.searchParams.get(`${advKey}-label`)!,
                        value: { id: url.searchParams.get(`${advKey}-id`)! },
                    },
                });
            }
            const rangeKey = key.split('-range')[0];
            if (searchBoxKeys.includes(rangeKey)) {
                const isoValues = url.searchParams.get(`${rangeKey}-range`)!.split('–');
                result.push({
                    optionName: rangeKey,
                    optionRelation: 'eq',
                    optionValue: [
                        isoValues[0] ? parseISO(isoValues[0]) : null,
                        isoValues[1] ? parseISO(isoValues[1]) : null,
                    ],
                });
            }
        }
    });

    return result;
};

const defaultValue: SearchBoxAutocompleteValue[] = [];
// eslint-disable-next-line no-empty-function, @typescript-eslint/no-empty-function
const defaultOnChange: (newValue: SearchBoxAutocompleteValue[]) => void = () => {};
export const SearchBox: (props: SearchBoxProps) => ReactElement = ({
    menuOptions,
    name,
    onChange: onChangeProp = defaultOnChange,
    value = defaultValue,
    onSearch,
    sx,
    useUrlSearchParams = true,
}) => {
    const isMount = useRef(true);
    const autocompleteInputRef = useRef<HTMLInputElement>(null!);
    const lastSegmentRef = useRef<HTMLDivElement>(null!);

    const initialValue = useMemo(() => {
        return useUrlSearchParams ? getValueFromUrl(name, menuOptions) : value;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [searchBoxValue, setSearchBoxValue] = useState<SearchBoxAutocompleteValue[]>(initialValue);

    const onChange = useCallback(
        (newValue: SearchBoxAutocompleteValue[]) => {
            onChangeProp(newValue);
            if (useUrlSearchParams) {
                const searchBoxKeys = [name, ...menuOptions.map(({ name: menuOptionName }) => menuOptionName)];
                const url = new URL(window.location.href);

                searchBoxKeys.forEach((key) => {
                    url.searchParams.delete(key);
                    url.searchParams.delete(`${key}-id`);
                    url.searchParams.delete(`${key}-label`);
                    url.searchParams.delete(`${key}-range`);
                });

                newValue.forEach((val) => {
                    if ('string' === typeof val.optionValue) {
                        url.searchParams.append(val.optionName, val.optionValue);
                    } else if (val.optionValue && 'value' in val.optionValue) {
                        if ('string' === typeof val.optionValue.value || 'number' === typeof val.optionValue.value) {
                            url.searchParams.append(`${val.optionName}`, `${val.optionValue.value}`);
                            url.searchParams.append(`${val.optionName}-label`, `${val.optionValue.label}`);
                        } else {
                            url.searchParams.append(`${val.optionName}-id`, `${val.optionValue.value.id}`);
                            url.searchParams.append(`${val.optionName}-label`, `${val.optionValue.label}`);
                        }
                    } else if (val.optionValue) {
                        url.searchParams.append(
                            `${val.optionName}-range`,
                            `${val.optionValue.map((v) => v?.toISOString()).join('–')}`,
                        );
                    }
                });
                window.history.pushState({}, '', url);
            }
        },
        [menuOptions, name, onChangeProp, useUrlSearchParams],
    );
    const [autocompleteValue, setAutocompleteValue] = useState<Array<SearchBoxMenuOption | string>>(
        prepareInitialDisplayValue(initialValue, menuOptions),
    );
    useEffect(() => {
        if (isMount.current) {
            isMount.current = false;
        } else {
            setAutocompleteValue((prevAutocompleteValue) => {
                return prevAutocompleteValue.map((val) => {
                    if ('string' !== typeof val) {
                        const updatedMenuOption = menuOptions.find((mOpt) => {
                            return val.name === mOpt.name;
                        })!;

                        return {
                            ...val,
                            optionValues: updatedMenuOption.optionValues,
                        };
                    }

                    return val;
                });
            });
        }
    }, [menuOptions]);

    const handleEditSegment = useCallback(
        (index: number, newValue?: SearchBoxAutocompleteValue, selectedOption?: SearchBoxMenuOption) => {
            setSearchBoxValue((prevSearchBoxValue) => {
                const newSearchBoxValue = [...prevSearchBoxValue];
                const indexInValues = getSegmentIndex(index, newSearchBoxValue);
                if (newValue?.optionValue) {
                    newSearchBoxValue.splice(indexInValues, 1, newValue);
                } else if (!!newValue && !!selectedOption && 'dateRange' === selectedOption.inputType) {
                    newSearchBoxValue.splice(indexInValues, 1, {
                        ...newValue,
                        optionValue: Array.isArray(newSearchBoxValue[indexInValues].optionValue)
                            ? newSearchBoxValue[indexInValues].optionValue
                            : undefined,
                    });
                } else if (!!newValue && !!selectedOption) {
                    newSearchBoxValue.splice(indexInValues, 1, {
                        ...newValue,
                        optionValue: undefined,
                    });
                } else {
                    newSearchBoxValue.splice(indexInValues, 1);
                }
                onChange(newSearchBoxValue);

                return newSearchBoxValue;
            });

            // update autocomplete display values
            if ('freeSolo' === newValue?.optionRelation) {
                setAutocompleteValue((prevAutocompleteValue) => {
                    const newAutocompleteValue = [...prevAutocompleteValue];
                    if (newValue.optionValue as string) {
                        newAutocompleteValue.splice(index, 1, newValue.optionValue as string);
                    } else {
                        newAutocompleteValue.splice(index, 1);
                    }

                    return newAutocompleteValue;
                });
                autocompleteInputRef.current.focus();
            } else if (!newValue && !selectedOption) {
                setAutocompleteValue((prevAutocompleteValue) => {
                    const newAutocompleteValue = [...prevAutocompleteValue];
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                    newAutocompleteValue.splice(index, 2);

                    return newAutocompleteValue;
                });
            } else if (selectedOption) {
                setAutocompleteValue((prevAutocompleteValue) => {
                    const newAutocompleteValue = [...prevAutocompleteValue];
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                    newAutocompleteValue.splice(index, 2, selectedOption, { ...selectedOption, isPlaceholder: true });

                    return newAutocompleteValue;
                });
            } else {
                autocompleteInputRef.current.focus();
            }
        },
        [onChange],
    );

    return (
        <Autocomplete
            classes={{ root: `search-box-${name}` }}
            id={`search-box-${name}-input`}
            multiple
            freeSolo
            getOptionLabel={(option) => {
                if ('string' === typeof option) {
                    return option;
                } else if ('string' === typeof option.menuLabel) {
                    return option.menuLabel;
                }

                return option.inputLabel;
            }}
            renderOption={(props, option) => {
                if ('string' !== typeof option && option.renderOption) {
                    return option.renderOption(props, option);
                }

                return (
                    <li {...props}>{'string' === typeof option ? option : option.menuLabel ?? option.inputLabel}</li>
                );
            }}
            options={menuOptions}
            componentsProps={{
                popper: {
                    anchorEl: () => {
                        return lastSegmentRef.current;
                    },
                    placement: 'bottom-start',
                    sx: {
                        width: 'auto! important',
                    },
                },
                clearIndicator: {
                    sx: {
                        visibility: 'visible',
                    },
                },
                popupIndicator: {
                    sx: {
                        display: 'none',
                    },
                },
            }}
            value={autocompleteValue}
            autoSelect
            filterSelectedOptions
            // blurOnSelect
            isOptionEqualToValue={(option, compareValue) => {
                return 'string' === typeof option || 'string' === typeof compareValue
                    ? option === compareValue
                    : option.name === compareValue.name;
            }}
            forcePopupIcon // to make sure that endAdornment will have value
            onChange={(event, newValue, reason, details) => {
                if ('selectOption' === reason || ('blur' === reason && 'string' !== typeof details?.option)) {
                    setAutocompleteValue([
                        ...newValue,
                        { ...(details?.option as SearchBoxMenuOption), isPlaceholder: true },
                    ] as SearchBoxMenuOption[]);

                    setSearchBoxValue((prevSearchBoxValue) => {
                        const newSearchBoxValue = [
                            ...prevSearchBoxValue,
                            {
                                optionName: (details?.option as SearchBoxMenuOption).name,
                                optionRelation: 'eq',
                            } satisfies SearchBoxAutocompleteValue,
                        ];
                        onChange(newSearchBoxValue);

                        return newSearchBoxValue;
                    });
                    // eslint-disable-next-line unicorn/prefer-switch
                } else if ('createOption' === reason || ('blur' === reason && 'string' === typeof details?.option)) {
                    setAutocompleteValue(newValue as SearchBoxMenuOption[]);
                    setSearchBoxValue((prevSearchBoxValue) => {
                        const newSearchBoxValue = [
                            ...prevSearchBoxValue,
                            {
                                optionName: name,
                                optionRelation: 'freeSolo',
                                optionValue: details?.option as string,
                            } satisfies SearchBoxAutocompleteValue,
                        ];
                        onChange(newSearchBoxValue);

                        return newSearchBoxValue;
                    });
                } else if ('removeOption' === reason) {
                    if ('string' === typeof details?.option) {
                        setAutocompleteValue(newValue as SearchBoxMenuOption[]);
                        setSearchBoxValue((prevSearchBoxValue) => {
                            const newSearchBoxValue = prevSearchBoxValue.filter((prevValue) => {
                                return prevValue.optionValue !== details.option;
                            });

                            onChange(newSearchBoxValue);

                            return newSearchBoxValue;
                        });
                    } else if (details?.option) {
                        const nameToRemove = details.option.name;
                        setAutocompleteValue(
                            newValue.filter((newAutocompleteValue) => {
                                return (
                                    'string' === typeof newAutocompleteValue ||
                                    newAutocompleteValue.name !== nameToRemove
                                );
                            }) as SearchBoxMenuOption[],
                        );
                        setSearchBoxValue((prevSearchBoxValue) => {
                            const newSearchBoxValue = prevSearchBoxValue.filter((prevValue) => {
                                return prevValue.optionName !== nameToRemove;
                            });
                            onChange(newSearchBoxValue);

                            return newSearchBoxValue;
                        });
                    }
                } else if ('clear' === reason) {
                    setAutocompleteValue(newValue as SearchBoxMenuOption[]);
                    onChange([]);
                    setSearchBoxValue([]);
                }
            }}
            // eslint-disable-next-line @typescript-eslint/naming-convention
            renderInput={({ ...params }) => {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const { InputProps, ...newParams } = {
                    ...params,
                    InputProps: {
                        ...params.InputProps,
                        startAdornment: [
                            ...(params.InputProps.startAdornment ? [params.InputProps.startAdornment] : []),
                            <Box key='boxLastSegment' sx={{ alignSelf: 'flex-end' }} ref={lastSegmentRef} />,
                        ],
                        endAdornment: cloneElement(
                            params.InputProps.endAdornment as ReactElement,
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                            (params.InputProps.endAdornment as ReactElement).props,
                            [
                                ...(onSearch
                                    ? [
                                          <IconButton
                                              key='search-button'
                                              aria-label='search'
                                              size='small'
                                              sx={{ p: '2px' }}
                                              tabIndex={-1}
                                              onClick={() => {
                                                  onSearch(searchBoxValue);
                                              }}
                                          >
                                              <Icon>search</Icon>
                                          </IconButton>,
                                      ]
                                    : []),
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                                (params.InputProps.endAdornment as ReactElement).props.children,
                            ],
                        ),
                    },
                };

                return (
                    <OutlinedInput
                        inputRef={autocompleteInputRef}
                        {...newParams}
                        size='small'
                        {...InputProps}
                        inputProps={{ ...params.inputProps, sx: { opacity: '1 !important' } }}
                        onKeyDown={(event) => {
                            if ('Enter' === event.key && '' === newParams.inputProps.value) {
                                onSearch?.(searchBoxValue);
                            }
                        }}
                    />
                );
            }}
            renderTags={(tagsValue, getTagProps) => {
                return tagsValue.map((option, index) => {
                    const { key, onDelete, className } = getTagProps({ index });

                    if ('string' === typeof option) {
                        return (
                            <SearchBoxTextSegment
                                key={key}
                                searchBoxName={name}
                                option={option}
                                onEdit={handleEditSegment}
                                index={index}
                                onDelete={onDelete}
                                className={className}
                            />
                        );
                    }

                    if (option.isPlaceholder) {
                        const indexInValues = getSegmentIndex(index, searchBoxValue);

                        return 'dateRange' === option.inputType ? (
                            <SearchBoxDateRangeSegment
                                key={key}
                                option={option}
                                onEdit={handleEditSegment}
                                index={index}
                                className={className}
                                value={searchBoxValue[indexInValues - 1]?.optionValue as DateRange<Date>}
                                onDelete={onDelete}
                                searchBoxName={name}
                            />
                        ) : (
                            <SearchBoxAutocompleteValueSegment
                                key={key}
                                option={option}
                                onEdit={handleEditSegment}
                                index={index}
                                className={className}
                                value={searchBoxValue[indexInValues - 1]?.optionValue as SearchBoxOptionValue}
                                onDelete={onDelete}
                                searchBoxName={name}
                            />
                        );
                    }

                    return (
                        <Fragment key={key}>
                            <SearchBoxAutocompleteLabelSegment
                                option={option}
                                onEdit={handleEditSegment}
                                index={index}
                                className={className}
                                options={menuOptions.filter((opt) => {
                                    return (
                                        opt.name === option.name ||
                                        searchBoxValue.every((optValue) => {
                                            return optValue.optionName !== opt.name;
                                        })
                                    );
                                })}
                            />
                            <Chip
                                size='medium'
                                label={'dateRange' === option.inputType ? '∈' : '='}
                                sx={{ borderRadius: 0 }}
                            />
                        </Fragment>
                    );
                });
            }}
            sx={sx}
        />
    );
};
